import { createContext, PropsWithChildren, useCallback, useRef, useState } from "react"
import { PayrollChangePopup } from "../../components/PayrollChangePopup"
import { DownloadPayrollBreakdown } from "../../components/DownloadPayrollBreakdown"
import { Company } from "@oysterhr/schema"
import { PayrollContextCompany, useGetCompanyDetails } from "./hooks/useGetCompanyDetails"
import { PayrollChangeRestrictedModal } from "../../components/PayrollChangePopup/components/PayrollChangeRestrictedModal"

interface OpenPayrollChangePopupProps {
  period?: string
  engagementId?: string
}

interface OpenDownloadBreakdownPopupProps {
  period: string
}

interface Props {
  companyId: string
  engagementId?: string
  refreshFn?: () => void
}

interface ContextType {
  companyId: string
  engagementId?: string
  openPayrollChangePopup: (params?: OpenPayrollChangePopupProps) => void
  openRestrictedPopup: () => void
  openDownloadBreakdownPopup: (params: OpenDownloadBreakdownPopupProps) => void
  setRefreshFn: (fn?: () => void) => void
  authorizations: Company["authorizations"]
  company?: PayrollContextCompany
}

const PayrollContext = createContext<ContextType>({} as ContextType)

function PayrollProvider({
  companyId,
  engagementId,
  refreshFn,
  children,
}: PropsWithChildren<Props>) {
  const [isPayrollChangePopupOpen, setIsPayrollChangePopupOpen] = useState(false)
  const [isRestrictedPopupOpen, setIsRestrictedPopupOpen] = useState(false)
  const [isDownloadBreakdownPopupOpen, setIsDownloadBreakdownPopupOpen] = useState(false)
  const [period, setPeriod] = useState<string | undefined>()
  const [selectedEngagementId, setSelectedEngagementId] = useState<string | undefined>(engagementId)
  const refresh = useRef(refreshFn)
  const { data } = useGetCompanyDetails({ companyId })
  const setRefreshFn = useCallback((fn?: () => void) => {
    refresh.current = fn
  }, [])

  const openPayrollChangePopup = useCallback(
    ({ period, engagementId }: OpenPayrollChangePopupProps = {}) => {
      if (engagementId) setSelectedEngagementId(engagementId)
      if (period) {
        const [year, month] = period.split("-")
        setPeriod(`${year}-${month.padStart(2, "0")}`)
      }

      setIsPayrollChangePopupOpen(true)
    },
    [],
  )

  const openDownloadBreakdownPopup = useCallback(({ period }: OpenDownloadBreakdownPopupProps) => {
    setPeriod(period)
    setIsDownloadBreakdownPopupOpen(true)
  }, [])

  const isTeamMemberAndDateReadonly = Boolean(engagementId)

  return (
    <PayrollContext.Provider
      value={{
        companyId,
        engagementId,
        openPayrollChangePopup,
        openRestrictedPopup: () => setIsRestrictedPopupOpen(true),
        openDownloadBreakdownPopup,
        setRefreshFn,
        authorizations: data?.company?.authorizations,
        company: data?.company,
      }}
    >
      {children}
      {isPayrollChangePopupOpen && (
        <PayrollChangePopup
          period={period}
          engagementId={selectedEngagementId}
          onChange={() => {
            refresh.current?.()
            setIsPayrollChangePopupOpen(false)
          }}
          onCancel={() => {
            setIsPayrollChangePopupOpen(false)
          }}
          isTeamMemberAndDateReadonly={isTeamMemberAndDateReadonly}
        />
      )}
      {isRestrictedPopupOpen && (
        <PayrollChangeRestrictedModal onClose={() => setIsRestrictedPopupOpen(false)} />
      )}

      {isDownloadBreakdownPopupOpen && period && (
        <DownloadPayrollBreakdown
          setShowModal={setIsDownloadBreakdownPopupOpen}
          period={period}
          companyId={companyId}
        />
      )}
    </PayrollContext.Provider>
  )
}

export { PayrollContext, PayrollProvider }
